// Components/Billing/BillingDashboard.jsx
import React, { useState, useEffect } from 'react';
import { CreditCard, History, AlertCircle, CheckCircle } from 'lucide-react';
import SubscriptionStatus from './SubscriptionStatus';
import PaymentForm from './PaymentForm';
import PaymentHistory from './PaymentHistory';
import { checkBuildingAccess } from '../../Fetch/fetchSubscription';
import styles from './BillingDashboard.module.css';

const BillingDashboard = ({ buildingId, userRole }) => {
    const [activeTab, setActiveTab] = useState('status');
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [buildingAccess, setBuildingAccess] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        checkAccess();
    }, [buildingId]);

    const checkAccess = async () => {
        try {
            setIsLoading(true);
            const accessResponse = await checkBuildingAccess(buildingId);
            setBuildingAccess(accessResponse);
        } catch (error) {
        } finally {
            setIsLoading(false);
        }
    };

    const handlePaymentNeeded = (subscription) => {
        setSubscriptionData(subscription);
        setShowPaymentForm(true);
    };

    const handlePaymentSuccess = (paymentResult) => {
        setShowPaymentForm(false);
        setSubscriptionData(null);
        // Refresh the subscription status
        window.location.reload(); // In production, you'd want to update state instead
    };

    const handleCancelPayment = () => {
        setShowPaymentForm(false);
        setSubscriptionData(null);
    };

    // Show loading state
    if (isLoading) {
        return (
            <div className={styles.billingDashboard} dir="rtl">
                <div className={styles.loading}>
                    <div className={styles.loadingSpinner}></div>
                    <p>טוען נתוני חיוב...</p>
                </div>
            </div>
        );
    }

    // Show payment form modal
    if (showPaymentForm && subscriptionData) {
        return (
            <div className={styles.billingDashboard} dir="rtl">
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <PaymentForm
                            subscription={subscriptionData}
                            onPaymentSuccess={handlePaymentSuccess}
                            onCancel={handleCancelPayment}
                        />
                    </div>
                </div>
            </div>
        );
    }

    // Show access denied if building is locked
    if (buildingAccess && !buildingAccess.hasAccess) {
        return (
            <div className={styles.billingDashboard} dir="rtl">
                <div className={styles.accessDenied}>
                    <AlertCircle className={styles.accessDeniedIcon} />
                    <h2>הגישה לבניין חסומה</h2>
                    <p className={styles.accessDeniedReason}>{buildingAccess.reason}</p>
                    <p className={styles.accessDeniedMessage}>
                        כדי להמשיך להשתמש במערכת, יש לעדכן את פרטי התשלום.
                    </p>
                    <button 
                        className={styles.unlockButton}
                        onClick={() => setActiveTab('status')}
                    >
                        <CreditCard size={16} />
                        עדכן תשלום
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.billingDashboard} dir="rtl">
            <div className={styles.dashboardHeader}>
                <h1 className={styles.dashboardTitle}>
                    <CreditCard className={styles.titleIcon} />
                    ניהול חיוב ומנויים
                </h1>
                <p className={styles.dashboardSubtitle}>
                    נהל את המנוי שלך ועקוב אחר תשלומים
                </p>
            </div>

            {/* Navigation Tabs */}
            <div className={styles.tabsContainer}>
                <button
                    className={`${styles.tab} ${activeTab === 'status' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('status')}
                >
                    <CheckCircle size={16} />
                    סטטוס מנוי
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'history' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('history')}
                >
                    <History size={16} />
                    היסטוריית תשלומים
                </button>
            </div>

            {/* Tab Content */}
            <div className={styles.tabContent}>
                {activeTab === 'status' && (
                    <SubscriptionStatus
                        buildingId={buildingId}
                        onPaymentNeeded={handlePaymentNeeded}
                    />
                )}
                
                {activeTab === 'history' && (
                    <PaymentHistory buildingId={buildingId} />
                )}
            </div>

            {/* Quick Actions */}
            <div className={styles.quickActions}>
                <h3 className={styles.quickActionsTitle}>פעולות מהירות</h3>
                <div className={styles.actionsGrid}>
                    <button 
                        className={styles.actionButton}
                        onClick={() => setActiveTab('status')}
                    >
                        <CheckCircle size={20} />
                        <span>בדוק סטטוס מנוי</span>
                    </button>
                    <button 
                        className={styles.actionButton}
                        onClick={() => setActiveTab('history')}
                    >
                        <History size={20} />
                        <span>צפה בתשלומים</span>
                    </button>
                    <button 
                        className={styles.actionButton}
                        onClick={() => handlePaymentNeeded({ 
                            subscriptionId: buildingId, 
                            monthlyPrice: 50 
                        })}
                    >
                        <CreditCard size={20} />
                        <span>ביצוע תשלום</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BillingDashboard;

